import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { buildHttpPost, buildHttpPostWithBody } from "./utils/backendInterface.js";
import { isDev } from "./utils/helper.js";
import * as XLSX from 'xlsx';

const CreateAccounts = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    let count = 0;
    let _isDev = isDev();
    let accessToken = sessionStorage.getItem("accessToken");
    if (!accessToken) {
      navigate('/sign-in');
    }

    const [accounts, setAccounts] = useState([
      { token: "", balance: "" , name: "" }
    ]);
    const [defaultBalance, setDefaultBalance] = useState("0");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadMessage, setUploadMessage] = useState("");

    const [companyId, setCompanyId] = useState(() => {
        // Initialize from URL or sessionStorage
        let pathArray = window.location.pathname.split('/');
        if (pathArray.length > 2 && pathArray[1] === "companies" && pathArray[2] !== undefined) {
            sessionStorage.setItem("lastSelectedCompany", pathArray[2]);
            return pathArray[2];
        } else {
            return sessionStorage.getItem("lastSelectedCompany");
        }
    });

    const [groupId, setGroupId] = useState(() => {
        // Initialize from URL or sessionStorage
        let pathArray = window.location.pathname.split('/');
        if (pathArray.length > 2 && pathArray[3] === "groups" && pathArray[4] !== undefined) {
            sessionStorage.setItem("lastSelectedGroup", pathArray[4]);
            return pathArray[4];
        } else {
            return sessionStorage.getItem("lastSelectedGroup");
        }
    });

    const handleChange = (index, field, value) => {
      const updated = [...accounts];
      updated[index][field] = value;
      setAccounts(updated);

      // Add new row automatically if last row has any data
      if (
        index === accounts.length - 1 &&
        (updated[index].token || updated[index].balance || updated[index].name)
      ) {
        setAccounts([
          ...updated,
          { token: "", balance: "", name: "" }
        ]);
      }
    };

    const handleExcelUpload = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Clear messages
      setError("");
      setSuccessMessage("");
      setUploadMessage("Processing Excel file...");

      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length === 0) {
            setError("❌ Excel file appears to be empty");
            setUploadMessage("");
            return;
          }

          // Process the data
          const headers = jsonData[0];
          const rows = jsonData.slice(1);

          // Find column indices (case-insensitive)
          const findColumnIndex = (columnNames) => {
            for (let name of columnNames) {
              const index = headers.findIndex(h => 
                h && h.toString().toLowerCase().trim() === name.toLowerCase()
              );
              if (index !== -1) return index;
            }
            return -1;
          };

          const tokenIndex = findColumnIndex(['token', 'tokens', 'account token']);
          const balanceIndex = findColumnIndex(['balance', 'balances', 'account balance']);
          const nameIndex = findColumnIndex(['name', 'names', 'account name']);

          if (tokenIndex === -1) {
            setError("❌ Excel file must have a 'Token' column");
            setUploadMessage("");
            return;
          }

          // Extract accounts from Excel data
          const importedAccounts = rows
            .filter(row => row[tokenIndex] && row[tokenIndex].toString().trim() !== '')
            .map(row => ({
              token: row[tokenIndex] ? row[tokenIndex].toString().trim() : '',
              balance: balanceIndex !== -1 && row[balanceIndex] ? 
                row[balanceIndex].toString().trim() : defaultBalance,
              name: nameIndex !== -1 && row[nameIndex] ? 
                row[nameIndex].toString().trim() : ''
            }));

          if (importedAccounts.length === 0) {
            setError("❌ No valid accounts found in Excel file");
            setUploadMessage("");
            return;
          }

          // Prepare accounts for API call
          const newAccountsJson = {
            companyId: companyId,
            groupId: groupId,
            accounts: importedAccounts,
          };

          function alertContents(httpRequest) {
              if (httpRequest && httpRequest.readyState === XMLHttpRequest.DONE) {
                  console.log("httpRequest.status: " + httpRequest.status);
                  setIsSubmitting(false); // hide loading state
                  
                  if (httpRequest.status === 201) {
                      // Success - parse response to get the number of accounts created
                      try {
                          const response = JSON.parse(httpRequest.responseText);
                          // Assuming the response contains an array of created accounts or a count
                          let accountCount = 0;
                          if (Array.isArray(response)) {
                              accountCount = response.length;
                          } else if (response.accounts && Array.isArray(response.accounts)) {
                              accountCount = response.accounts.length;
                          } else if (response.count !== undefined) {
                              accountCount = response.count;
                          } else {
                              // Fallback to the number of accounts we tried to create
                              accountCount = filledAccounts.length;
                          }
                          
                          setSuccessMessage(`✅ Successfully created ${accountCount} account${accountCount !== 1 ? 's' : ''}`);
                          setError("");
                          
                          // Optional: Clear the form after successful submission
                          setTimeout(() => {
                              setAccounts([{ token: "", balance: "", name: "" }]);
                          }, 1500);
                          
                      } catch (e) {
                          // If we can't parse the response, just show a generic success message
                          setSuccessMessage(`✅ Successfully created ${filledAccounts.length} account${filledAccounts.length !== 1 ? 's' : ''}`);
                          setError("");
                      }
                  } else {
                      // Error - display error message from response
                      let errorMessage = "❌ Failed to create accounts.";
                      try {
                          const errorResponse = JSON.parse(httpRequest.responseText);
                          if (errorResponse.message) {
                              errorMessage = `❌ ${errorResponse.message}`;
                          } else if (errorResponse.error) {
                              errorMessage = `❌ ${errorResponse.error}`;
                          } else if (typeof errorResponse === 'string') {
                              errorMessage = `❌ ${errorResponse}`;
                          }
                      } catch (e) {
                          // If response is not JSON, try to use it as plain text
                          if (httpRequest.responseText) {
                              errorMessage = `❌ ${httpRequest.responseText}`;
                          } else {
                              // Provide status-specific error messages
                              switch (httpRequest.status) {
                                  case 400:
                                      errorMessage = "❌ Bad Request: Invalid account data provided.";
                                      break;
                                  case 401:
                                      errorMessage = "❌ Unauthorized: Please sign in again.";
                                      break;
                                  case 403:
                                      errorMessage = "❌ Forbidden: You don't have permission to create accounts in this group.";
                                      break;
                                  case 404:
                                      errorMessage = "❌ Not Found: Company or group not found.";
                                      break;
                                  case 409:
                                      errorMessage = "❌ Conflict: One or more accounts already exist with the same token.";
                                      break;
                                  case 500:
                                      errorMessage = "❌ Server Error: Please try again later.";
                                      break;
                                  default:
                                      errorMessage = `❌ Request failed with status ${httpRequest.status}`;
                              }
                          }
                      }
                      
                      setError(errorMessage);
                      setSuccessMessage("");
                  }
              }
          }

          // Call the API directly here
          const requestParams = new Map();
          requestParams.set("accessToken", accessToken);

          const httpPost = buildHttpPostWithBody(
            `companies/${companyId}/groups/${groupId}/add-accounts`,
            requestParams,
            alertContents,
            JSON.stringify(newAccountsJson)
          );
          httpPost.sendRequest();
          
          setUploadMessage(`✅ Successfully imported ${importedAccounts.length} account${importedAccounts.length !== 1 ? 's' : ''} from Excel`);
          
          // Clear upload message after 3 seconds
          setTimeout(() => {
            setUploadMessage("");
          }, 3000);

        } catch (error) {
          console.error("Error processing Excel file:", error);
          setError("❌ Error processing Excel file. Please ensure it's a valid Excel file.");
          setUploadMessage("");
        }
      };

      reader.onerror = () => {
        setError("❌ Error reading file");
        setUploadMessage("");
      };

      reader.readAsArrayBuffer(file);
      
      // Reset file input so the same file can be selected again if needed
      event.target.value = '';
    };

    // Function to generate and download template (alternative to static file)
    const generateAndDownloadTemplate = () => {
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Create worksheet data with headers and one example row
      const wsData = [
        ['Token', 'Balance', 'Name'], // Headers
        ['example-token-001', '100', 'Account Name Example'] // Example row (optional)
      ];
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 20 }, // Token column width
        { wch: 15 }, // Balance column width
        { wch: 25 }  // Name column width
      ];
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Accounts');
      
      // Generate Excel file and trigger download
      XLSX.writeFile(wb, 'accounts-template.xlsx');
    };

    const onSubmitHandler = (event) => {
      event.preventDefault();
      console.log("onSubmitHandler CreateAccounts");

      let filledAccounts = accounts.filter(
        (t) => t.token.trim() !== "" // keep only rows with a token
      ).map(account => ({
        ...account,
        // Apply default balance if no balance is provided
        balance: account.balance.trim() === "" ? defaultBalance : account.balance
      }));

      if (filledAccounts.length === 0) {
        setError("⚠️ Please enter at least one account (Token required).");
        setSuccessMessage("");
        return;
      }

      setError(""); // clear error if validation passes
      setSuccessMessage(""); // clear previous success message
      setIsSubmitting(true); // show loading state

      let newAccountsJson = {
        companyId: companyId,
        groupId: groupId,
        accounts: filledAccounts,
      };

      function alertContents(httpRequest) {
          if (httpRequest && httpRequest.readyState === XMLHttpRequest.DONE) {
              console.log("httpRequest.status: " + httpRequest.status);
              setIsSubmitting(false); // hide loading state
              
              if (httpRequest.status === 201) {
                  // Success - parse response to get the number of accounts created
                  try {
                      const response = JSON.parse(httpRequest.responseText);
                      // Assuming the response contains an array of created accounts or a count
                      let accountCount = 0;
                      if (Array.isArray(response)) {
                          accountCount = response.length;
                      } else if (response.accounts && Array.isArray(response.accounts)) {
                          accountCount = response.accounts.length;
                      } else if (response.count !== undefined) {
                          accountCount = response.count;
                      } else {
                          // Fallback to the number of accounts we tried to create
                          accountCount = filledAccounts.length;
                      }
                      
                      setSuccessMessage(`✅ Successfully created ${accountCount} account${accountCount !== 1 ? 's' : ''}`);
                      setError("");
                      
                      // Optional: Clear the form after successful submission
                      setTimeout(() => {
                          setAccounts([{ token: "", balance: "", name: "" }]);
                      }, 1500);
                      
                  } catch (e) {
                      // If we can't parse the response, just show a generic success message
                      setSuccessMessage(`✅ Successfully created ${filledAccounts.length} account${filledAccounts.length !== 1 ? 's' : ''}`);
                      setError("");
                  }
              } else {
                  // Error - display error message from response
                  let errorMessage = "❌ Failed to create accounts.";
                  try {
                      const errorResponse = JSON.parse(httpRequest.responseText);
                      if (errorResponse.message) {
                          errorMessage = `❌ ${errorResponse.message}`;
                      } else if (errorResponse.error) {
                          errorMessage = `❌ ${errorResponse.error}`;
                      } else if (typeof errorResponse === 'string') {
                          errorMessage = `❌ ${errorResponse}`;
                      }
                  } catch (e) {
                      // If response is not JSON, try to use it as plain text
                      if (httpRequest.responseText) {
                          errorMessage = `❌ ${httpRequest.responseText}`;
                      } else {
                          // Provide status-specific error messages
                          switch (httpRequest.status) {
                              case 400:
                                  errorMessage = "❌ Bad Request: Invalid account data provided.";
                                  break;
                              case 401:
                                  errorMessage = "❌ Unauthorized: Please sign in again.";
                                  break;
                              case 403:
                                  errorMessage = "❌ Forbidden: You don't have permission to create accounts in this group.";
                                  break;
                              case 404:
                                  errorMessage = "❌ Not Found: Company or group not found.";
                                  break;
                              case 409:
                                  errorMessage = "❌ Conflict: One or more accounts already exist with the same token.";
                                  break;
                              case 500:
                                  errorMessage = "❌ Server Error: Please try again later.";
                                  break;
                              default:
                                  errorMessage = `❌ Request failed with status ${httpRequest.status}`;
                          }
                      }
                  }
                  
                  setError(errorMessage);
                  setSuccessMessage("");
              }
          }
      }

      let requestParams = new Map();
      requestParams.set("accessToken", accessToken);

      const httpPost = buildHttpPostWithBody(
          `companies/${companyId}/groups/${groupId}/add-accounts`,
          requestParams,
          alertContents,
          JSON.stringify(newAccountsJson)
      );
      httpPost.sendRequest();
    };

    useEffect(() => {
        count = count + 1;
        console.log("On page load: " + count);
        if (count === 1 && _isDev) {
            return;
        }
        if (!accessToken) {
            navigate('/sign-in');
            return;
        } else if (!sessionStorage.getItem("accessToken")) {
            return;
        }

        // Handle initial navigation if needed
        if (companyId && groupId && window.location.pathname !== `/companies/${companyId}/groups/${groupId}/create-accounts`) {
            console.log(`Re-navigation required to: /companies/${companyId}/groups/${groupId}/create-accounts`);
            navigate(`/companies/${companyId}/groups/${groupId}/create-accounts`);
        }
    }, [count]);

    return (
      <div id="create-accounts">
        <h2>Create Accounts</h2>
        
        <div className="content-box" style={{ position: "relative" }}>
          {/* Excel Upload and Download Buttons - positioned in top right */}
          <div style={{ 
            position: "absolute", 
            top: "20px", 
            right: "20px",
            zIndex: 10,
            display: "flex",
            gap: "12px",
            alignItems: "flex-start"
          }}>
            {/* Download Template Button */}
            <div>
              <button
                type="button"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/accounts-template.xlsx'; // Place file in public folder
                  link.download = 'accounts-template.xlsx';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.3s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#1976D2";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2196F3";
                }}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Download Template
              </button>
              <div style={{ 
                marginTop: "8px", 
                fontSize: "11px", 
                color: "#666",
                textAlign: "center"
              }}>
                Excel template
              </div>
            </div>

            {/* Upload Excel Button */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleExcelUpload}
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
                style={{
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: isSubmitting ? 0.6 : 1,
                  transition: "background-color 0.3s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = "#45a049";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#4CAF50";
                }}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Upload Excel
              </button>
              <div style={{ 
                marginTop: "8px", 
                fontSize: "11px", 
                color: "#666",
                textAlign: "center"
              }}>
                Supports .xlsx, .xls, .csv
              </div>
            </div>
          </div>

          {/* Upload Message */}
          {uploadMessage && (
            <div style={{
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: uploadMessage.includes("✅") ? "#f0f9ff" : "#fef3c7",
              border: uploadMessage.includes("✅") ? "1px solid #22c55e" : "1px solid #f59e0b",
              borderRadius: "4px",
              color: uploadMessage.includes("✅") ? "green" : "#92400e"
            }}>
              {uploadMessage}
            </div>
          )}

          {/* Default Balance Field */}
          <div style={{ marginBottom: "30px", display: "flex", alignItems: "center", paddingTop: "60px" }}>
            <label htmlFor="defaultBalance" style={{ marginRight: "10px", fontSize: "15px", color: "#333" }}>
              Default Balance (applied when row balance is empty):
            </label>
            <input
              id="defaultBalance"
              type="text"
              value={defaultBalance}
              onChange={(e) => setDefaultBalance(e.target.value)}
              style={{ padding: "8px", width: "120px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "15px" }}
            />
          </div>

          <form onSubmit={onSubmitHandler}>
            <table>
              <thead>
                <tr>
                  <th>Token *</th>
                  <th>Balance</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((t, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={t.token}
                        onChange={(e) =>
                          handleChange(index, "token", e.target.value)
                        }
                        disabled={isSubmitting}
                        style={{ 
                          width: "100%", 
                          padding: "5px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          opacity: isSubmitting ? 0.6 : 1
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={t.balance}
                        onChange={(e) =>
                          handleChange(index, "balance", e.target.value)
                        }
                        placeholder={defaultBalance}
                        disabled={isSubmitting}
                        style={{ 
                          width: "100%", 
                          padding: "5px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          opacity: isSubmitting ? 0.6 : 1
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={t.name}
                        onChange={(e) =>
                          handleChange(index, "name", e.target.value)
                        }
                        disabled={isSubmitting}
                        style={{ 
                          width: "100%", 
                          padding: "5px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          opacity: isSubmitting ? 0.6 : 1
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Success Message */}
            {successMessage && (
              <p style={{ 
                color: "green", 
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#f0f9ff",
                border: "1px solid #22c55e",
                borderRadius: "4px"
              }}>
                {successMessage}
              </p>
            )}
            
            {/* Error Message */}
            {error && (
              <p style={{ 
                color: "red", 
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#fef2f2",
                border: "1px solid #ef4444",
                borderRadius: "4px"
              }}>
                {error}
              </p>
            )}
            
            <br />
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
              style={{ 
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? "not-allowed" : "pointer"
              }}
            >
              {isSubmitting ? "Creating Accounts..." : "Submit Accounts"}
            </button>
          </form>
        </div>
      </div>
    );
};

export default CreateAccounts;
